<template lang="pug">
  .content-inner
    .field-container
      label Display status message
      aiq-switch(active-text="",
                 inactive-text="",
                 style="--aiq-switch-on-color: #30C28B; --aiq-switch-off-color: #C0CCDA"
                 name="busy_status.message_to_customer.display"
                 @change="onChange('display_message', $event)",
                 :disabled="disabled"
                 :model-value="displayMessage")

    .field-container.is-required
      label Status message
      .custom-rich-text-container
        rich-text(@update="onUpdateRichTextEditor"
                  :inChat="false"
                  :readOnlyMode="disabled || !displayMessage")

    aiq-row.field-container
      aiq-col(:span="12").sub-container
        label Inactivity timeout
        aiq-switch(@change="toggleInactivity"
                  style="--aiq-switch-on-color: #30C28B; --aiq-switch-off-color: #C0CCDA"
                  name="inactivity.enabled"
                  :disabled="disabled"
                  :model-value="inactivityConfig.enabled")
      aiq-col(v-if="inactivityConfig.enabled" :span="12")
        label After (minutes)
        aiq-input(v-model="localDuration"
                  size="small"
                  name="inactivity.duration"
                  placeholder="minutes"
                  :disabled="disabled"
                  @change="onInactivityDurationChange")

    aiq-row.field-container
      aiq-col(:span="12").sub-container
        label Hide closed conversations
        aiq-switch(
                  style="--aiq-switch-on-color: #30C28B; --aiq-switch-off-color: #C0CCDA"
                  :model-value="hideClosedConversations"
                  @update:modelValue="onHideClosedConversationsChange",
                  :disabled="disabled"
                  name="hide_closed_conversations")
    aiq-row.field-container(v-if="canEdit('/global/profile/email')")
      aiq-col(:span="12").sub-container
        label Enable email notifications
        aiq-switch(
                  style="--aiq-switch-on-color: #30C28B; --aiq-switch-off-color: #C0CCDA"
                  :model-value="enableEmail"
                  @update:modelValue="onEnableEmailChange",
                  :disabled="disabled"
                  name="enable_email")
    aiq-row.field-container(v-if="canEdit('/global/profile/campaigns') && featureFlags.ENABLE_AGENT_CAMPAIGN_NOTIFICATION")
      aiq-col(:span="12").sub-container
        label Enable campaign notifications
        aiq-switch(
                  style="--aiq-switch-on-color: #30C28B; --aiq-switch-off-color: #C0CCDA"
                  :model-value="enableCampaignNotifications"
                  @update:modelValue="onEnableCampaignNotificationsChange",
                  :disabled="disabled"
                  name="enable_campaign_notifications")

    aiq-row.field-container
      aiq-col(:span="12").sub-container
        label Enable video transcript notifications
        aiq-switch(
                  style="--aiq-switch-on-color: #30C28B; --aiq-switch-off-color: #C0CCDA"
                  :model-value="enableTranscriptNotifications"
                  @update:modelValue="onEnableTranscriptNotificationsChange",
                  :disabled="disabled"
                  name="enable_transcript_notifications")




</template>

<script>
import get from 'lodash/get';
import set from 'lodash/set';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { mapState } from 'vuex';
import mitt from 'mitt';
import RichText from '@/components/RichText/RichText.vue';

const requiredWhenDisplayed = (value, siblings) => {
  return siblings.displayMessage ? helpers.req(value) : true;
};

export default {
  components: {
    RichText,
  },
  provide() {
    return {
      richTextEventEmitter: this.richTextEventEmitter,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      displayMessage: { $autoDirty: true },
      messageText: { $autoDirty: true, requiredWhenDisplayed },
      localDuration: { $autoDirty: true },
      inactivityConfig: {
        enabled: { $autoDirty: true },
      },
      record: {
        preferences: {
          hide_closed_conversations: { $autoDirty: true },
          email_preference: { $autoDirty: true },
          campaign_notification_preference: { $autoDirty: true },
          transcript_notification_preference: { $autoDirty: true },
        },
      },
    };
  },
  props: {
    record: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
  },
  emits: ['change'],
  computed: {
    ...mapState({
      featureFlags: state => state.featureFlags.FEATURE_FLAGS,
    }),
    displayMessage() {
      return get(this.record, 'busy_status.message_to_customer.display', false);
    },

    messageText() {
      return get(this.record, 'busy_status.message_to_customer.text', '');
    },

    inactivityConfig() {
      return get(this.record, 'inactivity', {});
    },

    hideClosedConversations() {
      return get(this.record, 'preferences.hide_closed_conversations', false);
    },
    enableEmail() {
      return get(this.record, 'preferences.email_preference', true);
    },
    enableCampaignNotifications() {
      return get(this.record, 'preferences.campaign_notification_preference', true);
    },
    enableTranscriptNotifications() {
      return get(this.record, 'preferences.transcript_notification_preference', true);
    },
  },
  data() {
    return {
      localDuration: get(this.record, 'inactivity.duration', 0),
      richTextEventEmitter: mitt(),
      isInitializingMessageText: false,
    };
  },
  mounted() {
    this.isInitializingMessageText = true;
    this.$nextTick(() => {
      this.richTextEventEmitter.emit('setText', this.messageText);
      this.$nextTick(() => {
        this.isInitializingMessageText = false;
      });
    });
  },
  methods: {
    onChange(field, value) {
      const change = get(this.record, 'busy_status', {});
      if (field === 'display_message') {
        set(this.record, 'busy_status.message_to_customer.display', value);
      } else if (field === 'message_text') {
        set(this.record, 'busy_status.message_to_customer.text', value);
      }
      this.$emit('change', change);
    },

    onUpdateRichTextEditor(msgData) {
      const messageText = get(msgData, 'text', '') === '' ? '' : get(msgData, 'html', '');
      set(this.record, 'busy_status.message_to_customer.text', messageText);

      if (!this.isInitializingMessageText) {
        this.$emit('change', get(this.record, 'busy_status', {}));
      }
    },

    updateInactivityConfig(key, val) {
      set(this.record, `inactivity.${key}`, val);
      this.$emit('change', { key, val });
    },

    toggleInactivity() {
      this.updateInactivityConfig('enabled', !this.inactivityConfig.enabled);
    },

    onInactivityDurationChange(value) {
      try {
        const duration = parseInt(value, 10);
        if (Number.isNaN(duration)) {
          throw new Error(`${value} is not a number`);
        }
        this.localDuration = duration;
        this.updateInactivityConfig('duration', duration);
      } catch (err) {
        this.$aiq.notify.error(err.message);
      }
    },

    onHideClosedConversationsChange(value) {
      set(this.record, 'preferences.hide_closed_conversations', value);
      this.$emit('change', { key: 'hide_closed_conversations', value });
    },
    onEnableEmailChange(value) {
      set(this.record, 'preferences.email_preference', value);
      this.$emit('change', { key: 'email_preference', value });
    },
    onEnableCampaignNotificationsChange(value) {
      set(this.record, 'preferences.campaign_notification_preference', value);
      this.$emit('change', { key: 'campaign_notification_preference', value });
    },
    onEnableTranscriptNotificationsChange(value) {
      set(this.record, 'preferences.transcript_notification_preference', value);
      this.$emit('change', { key: 'transcript_notification_preference', value });
    },
  },
};
</script>

<style lang="scss" scoped>
.field-container {
  margin: 10px 5px;

  .el-switch {
    margin-left: 30px;
  }

  .sub-container {
    margin-top: 10px;
    margin-bottom: 14px;
  }

  .el-input {
    width: 50%;
    margin: 0px 0px 0px 30px;
  }

  .custom-rich-text-container {
    margin-top: 10px;
  }
}
</style>
